import { useDeferredValue, useState } from 'react';
import { Search } from 'lucide-react';
import { Spinner } from '../../../components/ui';
import { useSymbolSearch } from '../../../api/hooks';
import { isManualPickerSymbolSelectable } from './utils/manualSymbolCompatibility';

// Symbol-only result label: prefer display_name, fall back to the trading symbol, and
// strip any "EXCHANGE:" prefix so a full path (e.g. MCX:GOLD26AUGFUT) never leaks into
// the list — the raw path is only used when it's the sole text available.
function symbolLabel(row) {
  const raw = String(row?.display_name || row?.symbol || '').trim();
  return raw.includes(':') ? raw.split(':').pop() : raw;
}

// Mobile-only inline Add Symbol search (M4A) — expands above the watchlist rows below lg.
// Mirrors components/SymbolPickerModal.jsx's search/debounce/result rendering (same
// useSymbolSearch hook, same useDeferredValue debounce mechanism), as an inline block
// instead of a portal/modal. Always locked to the strategy's own exchange/segment —
// Manual Trading strategies always fix these, matching the shared modal's own locked mode.
export default function InlineAddSymbolSearch({ strategy, riskUnit, onAddSymbolSelect, onClose }) {
  const mt = strategy.manual_trading || {};
  const exchange = mt.exchange;
  const segment = String(mt.segment || '').toLowerCase();

  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  // Only search / show results once the user has typed at least 2 characters, so an empty
  // input stays compact and no result list appears before typing.
  const hasQuery = deferredQuery.trim().length >= 2;
  const searchQ = useSymbolSearch(deferredQuery, segment, exchange, hasQuery);
  const rows = hasQuery ? (searchQ.data || []) : [];
  const selectableRows = rows.filter((row) => isManualPickerSymbolSelectable(mt, row));

  const handleSelect = (row) => {
    onAddSymbolSelect({ ...strategy, selectedRiskUnit: riskUnit }, row);
    setQuery(''); // clear so results collapse and the input is ready for the next add
    onClose?.();
  };

  return (
    <div>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-txt-muted" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${exchange || ''} symbol…`.replace('  ', ' ')}
          className="input-field !pl-9 !h-9 w-full text-xs"
        />
      </div>

      {hasQuery && (
        <div className="mt-1 max-h-60 overflow-y-auto">
          {searchQ.isLoading ? (
            <div className="py-4 flex justify-center items-center gap-2 text-txt-muted">
              <Spinner size={14} /> <span className="text-2xs font-mono">Searching…</span>
            </div>
          ) : searchQ.isError ? (
            <div className="px-3 py-4 text-2xs font-mono text-bear text-center">
              Failed to load symbols. Please retry.
            </div>
          ) : selectableRows.length === 0 ? (
            <div className="px-3 py-4 text-2xs font-mono text-txt-muted text-center">
              No symbols match your search.
            </div>
          ) : (
            <div className="divide-y divide-bg-border/50">
              {selectableRows.map((row) => (
                <button
                  key={`${row.symbol}-${row.segment}`}
                  type="button"
                  onClick={() => handleSelect(row)}
                  className="w-full text-left px-3 py-3 min-h-[44px] hover:bg-bg-hover active:bg-bg-hover/70 transition-colors"
                >
                  <span className="block text-sm font-mono font-semibold text-txt-primary truncate">
                    {symbolLabel(row)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
